import React, { useState, useRef, useEffect, FC } from 'react';
import { style } from 'typestyle';
import { ADALONG_COLORS } from '@src/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

interface FilterOption {
  value: string;
  label: string;
  selected: boolean;
}

interface SortOption {
  value: string;
  label: string;
}

interface TableFilterProps {
  type: 'filter' | 'sort';
  options: FilterOption[] | SortOption[];
  value?: string | string[];
  onFilterChange?: (selected: string[]) => void;
  onSortChange?: (sortBy: string, direction: 'asc' | 'desc') => void;
  currentSortBy?: string;
  currentSortDirection?: 'asc' | 'desc';
  placeholder?: string;
}

export const TableFilter: FC<TableFilterProps> = ({
  type,
  options,
  value,
  onFilterChange,
  onSortChange,
  currentSortBy,
  currentSortDirection,
  placeholder = 'Filter...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = type === 'filter' 
    ? (options as FilterOption[]).filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  const selectedCount = type === 'filter' 
    ? (options as FilterOption[]).filter(option => option.selected).length
    : 0;

  const handleFilterToggle = (optionValue: string) => {
    if (type === 'filter' && onFilterChange) {
      const updatedOptions = (options as FilterOption[]).map(option => 
        option.value === optionValue 
          ? { ...option, selected: !option.selected }
          : option
      );
      const selected = updatedOptions.filter(option => option.selected).map(option => option.value);
      onFilterChange(selected);
    }
  };

  const handleSortClick = (sortValue: string) => {
    if (type === 'sort' && onSortChange) {
      if (currentSortBy === sortValue) {
        // Toggle direction
        const newDirection = currentSortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange(sortValue, newDirection);
      } else {
        // New sort field, default to desc for date
        onSortChange(sortValue, sortValue === 'date' ? 'desc' : 'asc');
      }
      setIsOpen(false);
    }
  };

  const handleSelectAll = () => {
    if (type === 'filter' && onFilterChange) {
      const allSelected = (options as FilterOption[]).every(option => option.selected);
      const newSelected = allSelected ? [] : (options as FilterOption[]).map(option => option.value);
      onFilterChange(newSelected);
    }
  };

  const getSortIcon = () => {
    if (currentSortBy && currentSortDirection) {
      return currentSortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />;
    }
    return null;
  };

  return (
    <div className={containerClass} ref={dropdownRef}>
      <div className={triggerClass} onClick={() => setIsOpen(!isOpen)}>
        {type === 'sort' && getSortIcon()}
        <ArrowDropDownIcon fontSize="small" className={arrowClass} />
        {type === 'filter' && selectedCount > 0 ? (
          <span className={badgeClass}>{selectedCount}</span>
        ) : null}
      </div>
      
      {isOpen ? (
        <div className={dropdownClass}>
          {type === 'filter' ? (
            <>
              <div className={searchClass}>
                <input
                  type="text"
                  placeholder={`Search ${placeholder.toLowerCase()}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={searchInputClass}
                />
              </div>
              <div className={selectAllClass} onClick={handleSelectAll}>
                <CheckBoxIcon fontSize="small" />
                <span>Select All</span>
              </div>
              <div className={dividerClass} />
            </>
          ) : null}

          <div className={optionsClass}>
            {filteredOptions.map((option) => (
              <div
                key={option.value}
                className={optionClass}
                onClick={() => type === 'filter' ? handleFilterToggle(option.value) : handleSortClick(option.value)}
              >
                {type === 'filter' ? (
                  <>
                    {(option as FilterOption).selected ? (
                      <CheckBoxIcon fontSize="small" className={checkboxClass} />
                    ) : (
                      <CheckBoxOutlineBlankIcon fontSize="small" className={checkboxClass} />
                    )}
                    <span>{option.label}</span>
                  </>
                ) : (
                  <span className={sortOptionClass}>
                    {option.label}
                    {currentSortBy === option.value ? (
                      <span className={sortDirectionClass}>
                        {currentSortDirection === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                      </span>
                    ) : null}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      ) : null }
    </div>
  );
};

const containerClass = style({
  position: 'relative',
  display: 'inline-block',
});

const triggerClass = style({
  position: 'relative',
  width: 18,
  height: 18,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: ADALONG_COLORS.LIGHT_GRAY,
  transition: 'color 0.2s',
  $nest: {
    '&:hover': {
      color: ADALONG_COLORS.SMOOTH_WHITE,
    },
  },
});

const arrowClass = style({
  fontSize: '20px !important',
  color: ADALONG_COLORS.SMOOTH_WHITE,
});

const badgeClass = style({
  position: 'absolute',
  top: -6,
  right: -6,
  background: ADALONG_COLORS.FLASH_BLUE,
  color: 'white',
  borderRadius: '50%',
  minWidth: 16,
  height: 16,
  fontSize: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
});

const dropdownClass = style({
  position: 'absolute',
  top: '100%',
  left: -150,
  width: 200,
  maxHeight: 300,
  background: ADALONG_COLORS.SMOOTH_BLACK,
  border: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
  borderRadius: 6,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
  zIndex: 1000,
  overflow: 'hidden',
});

const searchClass = style({
  padding: 8,
  borderBottom: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
});

const searchInputClass = style({
  width: '100%',
  background: ADALONG_COLORS.DARK_GRAY,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: `1px solid ${ADALONG_COLORS.DARKER_GRAY}`,
  borderRadius: 4,
  padding: '6px 8px',
  fontSize: 12,
  outline: 'none',
  $nest: {
    '&:focus': {
      borderColor: ADALONG_COLORS.LIGHT_GRAY,
    },
  },
});

const selectAllClass = style({
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  fontSize: 13,
  color: ADALONG_COLORS.LIGHT_GRAY,
  fontWeight: 600,
  $nest: {
    '&:hover': {
      background: ADALONG_COLORS.DARK_GRAY,
      color: ADALONG_COLORS.SMOOTH_WHITE,
    },
  },
});

const dividerClass = style({
  height: 1,
  background: ADALONG_COLORS.DARKER_GRAY,
});

const optionsClass = style({
  maxHeight: 200,
  overflowY: 'auto',
});

const optionClass = style({
  padding: '8px 12px',
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  cursor: 'pointer',
  fontSize: 13,
  color: ADALONG_COLORS.SMOOTH_WHITE,
  $nest: {
    '&:hover': {
      background: ADALONG_COLORS.DARK_GRAY,
    },
  },
});

const checkboxClass = style({
  color: ADALONG_COLORS.SMOOTH_WHITE,
});

const sortOptionClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

const sortDirectionClass = style({
  display: 'flex',
  alignItems: 'center',
  color: ADALONG_COLORS.SMOOTH_WHITE,
});

export default TableFilter;
